import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { SowingReminders } from "@/components/SowingReminders";
import { FieldStatusBoard } from "@/components/FieldStatusBoard";
import { OutbreakAlerts } from "@/components/OutbreakAlerts";
import { InstallPrompt } from "@/components/InstallPrompt";
import { NotificationToggle } from "@/components/NotificationToggle";
import { useAuth } from "@/context/AuthContext";
import apiClient from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { TestTube, Bug, Map, CloudSun, ArrowRight, Sprout, MapPin } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1557139582-4206cd15c69a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxsdXNoJTIwYWdyaWN1bHR1cmFsJTIwZmFybSUyMGZpZWxkJTIwYWVyaWFsJTIwY3JvcHN8ZW58MHx8fHwxNzg5MTkxODg4fDA&ixlib=rb-4.1.0&q=85";

const actions = [
  { to: "/app/analyze", icon: TestTube, title: "Soil Diagnostic", desc: "Test values, photo or plain words", color: "#2D6A4F" },
  { to: "/app/pest", icon: Bug, title: "Pest Doctor", desc: "Photograph a sick leaf or bug", color: "#D97706" },
  { to: "/app/fields", icon: Map, title: "Saved Fields", desc: "Revisit your soil records", color: "#3B82F6" },
  { to: "/app/map", icon: MapPin, title: "Field Map", desc: "Pins with live weather & reminders", color: "#B91C1C" },
  { to: "/app/weather", icon: CloudSun, title: "Weather Advice", desc: "Daily farm guidance", color: "#8B5CF6" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);

  useEffect(() => { apiClient.get("/fields").then((r) => setFields(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero */}
        <div className="relative rounded-3xl overflow-hidden mb-8 border border-stone-200">
          <img src={heroImg} alt="Farm" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-emerald-950/70" />
          <div className="relative z-10 p-8 sm:p-12">
            <p className="text-emerald-200/80 text-sm uppercase tracking-wider font-semibold mb-2">{user?.farm_name || "Your farm"}</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4">
              Welcome back, {user?.name?.split(" ")[0]} 🌱
            </h1>
            <p className="text-emerald-100/80 max-w-lg mb-6">Study your ground and discover exactly what to plant and how to improve your soil.</p>
            <Button data-testid="dashboard-analyze-cta" onClick={() => navigate("/app/analyze")} className="bg-[#52B788] hover:bg-[#40916C] text-emerald-950 rounded-full px-6 h-12 font-semibold">
              Start a soil analysis <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <div className="font-mono-tech text-3xl font-bold text-emerald-950" data-testid="stat-fields">{fields.length}</div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mt-1">Saved fields</div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <div className="font-mono-tech text-3xl font-bold text-emerald-950">AI</div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mt-1">Powered advice</div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6 col-span-2 sm:col-span-1">
            <div className="font-mono-tech text-3xl font-bold text-emerald-950">6</div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mt-1">Soil types supported</div>
          </div>
        </div>

        {/* Alerts + install */}
        <OutbreakAlerts />
        <InstallPrompt />
        <NotificationToggle />

        {/* Field status */}
        <FieldStatusBoard fields={fields} />

        {/* Sowing reminders */}
        <SowingReminders fields={fields} />

        {/* Actions */}
        <h2 className="font-display text-2xl font-bold text-emerald-900 mb-4">Quick actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {actions.map((a, i) => (
            <button key={i} data-testid={`action-${i}`} onClick={() => navigate(a.to)}
              className="text-left bg-white rounded-2xl border border-stone-200 p-6 hover:border-[#52B788] hover:shadow-lg transition-all group">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${a.color}1a` }}>
                <a.icon className="h-6 w-6" style={{ color: a.color }} />
              </div>
              <h3 className="font-display text-lg font-semibold text-emerald-950 mb-1">{a.title}</h3>
              <p className="text-sm text-stone-600">{a.desc}</p>
            </button>
          ))}
        </div>

        {/* Recent */}
        {fields.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold text-emerald-900">Recent fields</h2>
              <Button variant="ghost" onClick={() => navigate("/app/fields")} className="text-[#2D6A4F]">View all</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {fields.slice(0, 3).map((f) => (
                <div key={f.id} className="bg-white rounded-2xl border border-stone-200 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sprout className="h-4 w-4 text-[#2D6A4F]" />
                    <span className="font-semibold text-emerald-950">{f.name}</span>
                  </div>
                  <div className="text-sm text-stone-500">{f.location || "No location"}</div>
                  <div className="mt-3 font-mono-tech text-sm text-stone-600">pH {f.soil?.ph} · {f.soil?.soil_type} · Score {Math.round(f.analysis?.soil_health?.score || 0)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
